import React from 'react';
import { SrOnly } from '../SrOnly';
import { findChildOfType, isChildType } from '~/utils';

export interface OverlayProps {
  /** The content to hide behind the overlay. */
  children: React.ReactNode;

  /**
   * The content to read to a screen reader, in place of the content hidden by
   * the overlay.  This prop is not necessary if the content rendered inside the
   * overlay sufficiently describes the content beneath the overlay
   */
  description?: string;

  /** If true, the overlaid content will not be rendered. */
  disabled?: boolean;
}

/**
 * Overlay is a component that will hide its children behind a white faded
 * rectangle. It does this in an accessible way, such that the content is hidden
 * from screen readers and keyboards
 *
 * ## Accessibility ♿
 * - The description prop is a string that will be read to screen reader users. It should be worded such
 *    that a user with a screen reader understands that the content is unavailable until some action is
 *    taken. This prop is not necessary if the content rendered inside the overlay sufficiently describes
 *    the content beneath the overlay
 * - Interactive controls (buttons, links, inputs...) within the overlay will be unreachable via keyboard.
 */
export function Overlay({ children, description, disabled = false }: OverlayProps) {
  const ref = React.useRef<HTMLFieldSetElement>(null);
  const overlayChild = findChildOfType(children, Overlay.Content);
  const remainingChildren = React.Children.toArray(children).filter(
    child => !isChildType(child, Overlay.Content),
  );

  const isRefAvailable = !!ref.current;

  const toggleLinks = (disableLinks = true) => {
    if (ref.current) {
      ref.current?.querySelectorAll('a').forEach(el => {
        if (disableLinks) {
          el.setAttribute('data-cds-overlay-element', 'true');
          el.setAttribute('data-cds-overlay-aria-hidden', el.getAttribute('aria-hidden') ?? '');
          el.setAttribute('data-cds-overlay-tabindex', el.getAttribute('tabindex') ?? '');
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('tabindex', '-1');
        } else {
          if (el.getAttribute('data-cds-overlay-element')) {
            const hiddenAttr = el.getAttribute('data-cds-overlay-aria-hidden');
            if (hiddenAttr) {
              el.setAttribute('aria-hidden', hiddenAttr);
            } else {
              el.removeAttribute('aria-hidden');
            }

            const tabIndexAttr = el.getAttribute('data-cds-overlay-tabindex');
            if (tabIndexAttr) {
              el.setAttribute('tabindex', tabIndexAttr);
            } else {
              el.removeAttribute('tabindex');
            }
          }
        }
      });
    }
  };

  React.useEffect(() => {
    toggleLinks(!disabled);
    return () => {
      toggleLinks(false);
    };
  }, [children, disabled, isRefAvailable]);

  return (
    <div css={{ position: disabled ? undefined : 'relative' }}>
      {!disabled && !!description && <SrOnly>{description}</SrOnly>}

      <fieldset
        aria-hidden={disabled ? undefined : 'true'}
        css={{
          border: 'none',
          margin: 0,
          padding: 0,
        }}
        disabled={!disabled}
        ref={ref}
        role="presentation"
      >
        {remainingChildren}
      </fieldset>

      {!disabled && overlayChild}
    </div>
  );
}

export interface OverlayContentProps {
  /** The overlaid content to render */
  children: React.ReactNode;

  /**
   * The type of background fade to apply to the overlay.
   *
   * - `linear` will apply a top-to-bottom white gradient to the background
   * - `default` will apply a consistent faded white background to the overlay
   */
  fade?: 'linear' | 'default';
}

Overlay.Content = function OverlayContent({ children, fade }: OverlayContentProps) {
  return (
    <div
      css={{
        background:
          fade === 'linear'
            ? `linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 50%)`
            : 'rgba(255,255,255,0.9)',
        display: 'flex',
        height: '100%',
        left: 0,
        position: 'absolute',
        top: 0,
        width: '100%',
      }}
    >
      {children}
    </div>
  );
};
